import { get_token } from "@/router";
import TariffRatesEditTab from "@/components/TariffRatesEditTab/TariffRatesEditTab.vue";
import {
    extract_rate_records,
    format_api_errors,
    normalize_rate_record,
    safe_parse_maybe_double_encoded_json,
    tariff_rates_endpoint
} from "@/components/TariffBookRates/tariff_book_rates_utils";

function get_today_iso_date() {
    return new Date().toISOString().slice(0, 10);
}

function add_days_to_iso_date(iso_date, days_to_add) {
    const base_date = new Date(`${iso_date}T00:00:00`);

    if (Number.isNaN(base_date.getTime())) {
        return get_today_iso_date();
    }

    base_date.setDate(base_date.getDate() + days_to_add);
    return base_date.toISOString().slice(0, 10);
}

function normalize_ajax_payload(payload, fallback_text = "") {
    if (payload === null || payload === undefined || payload === "") {
        return fallback_text
            ? safe_parse_maybe_double_encoded_json(fallback_text, null)
            : null;
    }

    if (typeof payload === "string") {
        return safe_parse_maybe_double_encoded_json(payload, payload);
    }

    return payload;
}

function handle_ajax_success(resolve, response_data, jqXHR) {
    resolve(normalize_ajax_payload(response_data, jqXHR?.responseText || ""));
}

function handle_ajax_error(reject, jqXHR) {
    const error_payload = normalize_ajax_payload(
        jqXHR?.responseJSON ?? jqXHR?.responseText,
        jqXHR?.responseText || ""
    );

    reject(new Error(format_api_errors(error_payload)));
}

function build_ajax_options(url, options, resolve, reject) {
    return ({
        url,
        method: options.method || "GET",
        headers: options.headers || {},
        contentType: options.contentType || options.headers?.["Content-Type"] || undefined,
        data: options.body,
        processData: options.processData ?? false,
        success(response_data, _text_status, jqXHR) {
            handle_ajax_success(resolve, response_data, jqXHR);
        },
        error(jqXHR) {
            handle_ajax_error(reject, jqXHR);
        }
    });
}

function ajax_json_request(url, options = {}) {
    return new Promise((resolve, reject) => {
        const jquery_instance = window.jQuery || window.$;

        if (!jquery_instance?.ajax) {
            reject(new Error("jQuery AJAX is not available."));
            return;
        }

        jquery_instance.ajax(build_ajax_options(url, options, resolve, reject));
    });
}

export default {
    name: "TariffBookRatesModal",

    components: {
        TariffRatesEditTab
    },

    props: {
        is_open: {
            type: Boolean,
            default: false
        },
        tariff_book: {
            type: Object,
            default: null
        },
        context: {
            type: Object,
            default: null
        },
        initial_tab: {
            type: String,
            default: "edit"
        }
    },

    emits: ["close", "updated"],

    data() {
        return ({
            is_loading: false,
            is_saving: false,
            load_error: "",
            save_error: "",
            save_success: "",
            snackbar_timeout_id: null,
            rate_rows: []
        });
    },

    computed: {
        has_tariff_book() {
            return Boolean(this.tariff_book?.id);
        },

        supports_rate_updates() {
            return Boolean(this.get_rates_base_url());
        },

        modal_title() {
            if (!this.tariff_book?.name) {
                return "Edit Tariff Rates";
            }

            return `Edit ${this.tariff_book.name} Rates`;
        },

        active_rate_count() {
            return this.rate_rows.filter((rate) => rate.is_active).length;
        },

        modal_subtitle() {
            const property_name = this.context?.property_name || this.tariff_book?.name || "Unknown tariff book";
            const tariff_name = this.context?.tariff_name || this.rate_rows[0]?.name || "Unknown tariff";

            return `${property_name} - ${tariff_name}`;
        }
    },

    watch: {
        is_open: {
            immediate: true,
            async handler(is_open) {
                if (is_open && this.has_tariff_book) {
                    await this.load_rates();
                    return;
                }

                if (!is_open) {
                    this.reset_state();
                }
            }
        },

        tariff_book: {
            deep: false,
            async handler(new_tariff_book, previous_tariff_book) {
                if (!this.is_open) {
                    return;
                }

                if (new_tariff_book?.id && new_tariff_book?.id !== previous_tariff_book?.id) {
                    await this.load_rates();
                }
            }
        }
    },

    beforeUnmount() {
        this.clear_snackbar_timer();
    },

    methods: {
        clear_snackbar_timer() {
            if (this.snackbar_timeout_id) {
                clearTimeout(this.snackbar_timeout_id);
                this.snackbar_timeout_id = null;
            }
        },

        start_snackbar_timer() {
            this.clear_snackbar_timer();
            this.snackbar_timeout_id = setTimeout(() => {
                this.load_error = "";
                this.save_error = "";
                this.save_success = "";
                this.snackbar_timeout_id = null;
            }, 5000);
        },

        clear_load_error() {
            this.clear_snackbar_timer();
            this.load_error = "";
        },

        clear_save_error() {
            this.clear_snackbar_timer();
            this.save_error = "";
        },

        clear_save_success() {
            this.clear_snackbar_timer();
            this.save_success = "";
        },

        set_load_error(message) {
            this.load_error = message || "";
            if (message) {
                this.start_snackbar_timer();
            }
        },

        set_save_error(message) {
            this.save_error = message || "";
            this.save_success = "";
            if (message) {
                this.start_snackbar_timer();
            }
        },

        set_save_success(message) {
            this.save_success = message || "";
            this.save_error = "";
            if (message) {
                this.start_snackbar_timer();
            }
        },

        reset_state() {
            this.clear_snackbar_timer();
            this.is_loading = false;
            this.is_saving = false;
            this.load_error = "";
            this.save_error = "";
            this.save_success = "";
            this.rate_rows = [];
        },

        close_modal() {
            this.$emit("close");
        },

        get_auth_headers(include_json_content_type = true) {
            const token = get_token();
            const headers = {
                Accept: "application/json"
            };

            if (include_json_content_type) {
                headers["Content-Type"] = "application/json";
            }

            if (token) {
                headers.Authorization = `Token ${token}`;
            }

            return headers;
        },

        async fetch_rates_json(action_name, rate_id, options = {}) {
            const request_url = this.get_rate_request(action_name, rate_id);

            if (!request_url) {
                throw new Error("A valid tariff rate endpoint is required.");
            }

            return ajax_json_request(request_url, options);
        },

        get_rates_base_url() {
            return tariff_rates_endpoint;
        },

        get_rates_list_url() {
            const base_url = this.get_rates_base_url();

            if (!base_url) {
                return "";
            }

            return base_url;
        },

        get_tariff_rate_create_url(tariff_id) {
            const normalized_tariff_id = Number(tariff_id);

            if (!Number.isInteger(normalized_tariff_id) || normalized_tariff_id <= 0) {
                return "";
            }

            return this.get_rates_list_url().replace(/rates\/?$/i, `tariffs/${normalized_tariff_id}/rates/create/`);
        },

        get_context_tariff_id() {
            const tariff_id = Number(this.context?.tariff_id);

            return Number.isFinite(tariff_id) && tariff_id > 0 ? tariff_id : null;
        },

        get_tariff_detail_url(tariff_id) {
            const normalized_tariff_id = Number(tariff_id);

            if (!Number.isInteger(normalized_tariff_id) || normalized_tariff_id <= 0) {
                return "";
            }

            return this.get_rates_list_url().replace(/rates\/?$/i, `tariffs/${normalized_tariff_id}/`);
        },

        async fetch_tariff_detail_json(tariff_id, options = {}) {
            const detail_url = this.get_tariff_detail_url(tariff_id);

            if (!detail_url) {
                throw new Error("A valid tariff endpoint is required.");
            }

            return ajax_json_request(detail_url, options);
        },

        get_rate_request(action_name, rate_id) {
            if (action_name === "list") {
                const tariff_id = this.get_context_tariff_id();
                const list_url = tariff_id
                    ? `${this.get_rates_list_url()}?tariff_id=${tariff_id}`
                    : this.get_rates_list_url();
                return list_url;
            }

            if (action_name === "create") {
                return this.get_rates_list_url();
            }

            if (action_name === "update") {
                const update_url = this.get_tariff_rate_create_url(rate_id);

                if (!update_url) {
                    return null;
                }

                return update_url;
            }

            return null;
        },

        get_metric_rows(rate) {
            return Object.entries(rate?.rates || {}).map(([key, value]) => ({
                key,
                value
            }));
        },

        filter_rates_for_selected_property(rate_rows) {
            return rate_rows;
        },

        format_date(date_string) {
            if (!date_string) {
                return "Open ended";
            }

            const date = new Date(date_string);

            if (Number.isNaN(date.getTime())) {
                return date_string;
            }

            return new Intl.DateTimeFormat("en-ZA", {
                year: "numeric",
                month: "short",
                day: "2-digit"
            }).format(date);
        },

        async load_rates({ preserve_save_success = false } = {}) {
            if (!this.has_tariff_book || !this.get_rates_base_url()) {
                this.set_load_error("Tariff rates endpoint is not configured.");
                return;
            }

            this.is_loading = true;
            this.load_error = "";
            this.save_error = "";
            if (!preserve_save_success) {
                this.save_success = "";
            }

            try {
                const list_payload = await this.fetch_rates_json(
                    "list",
                    null,
                    {
                        method: "GET",
                        headers: this.get_auth_headers(false)
                    }
                );

                const listed_rate_records = extract_rate_records(list_payload);

                const matching_rate_records = listed_rate_records
                    .map((rate) => normalize_rate_record(rate))
                    .filter((rate) => {
                        return Boolean(rate);
                    });
                const filtered_rate_records = this.filter_rates_for_selected_property(matching_rate_records);

                this.rate_rows = [...filtered_rate_records].sort((first_rate, second_rate) => {
                    const first_effective_from = new Date(first_rate?.effective_from || 0).getTime();
                    const second_effective_from = new Date(second_rate?.effective_from || 0).getTime();

                    return second_effective_from - first_effective_from;
                });
            } catch (error) {
                this.set_load_error(error.message || "Unable to load tariff rates.");
            } finally {
                this.is_loading = false;
            }
        },

        update_metric_value(rate, metric_key, next_value) {
            const numeric_value = Number(next_value);
            rate.rates = {
                ...rate.rates,
                [String(metric_key)]: Number.isFinite(numeric_value) ? numeric_value : 0
            };
        },

        update_rate_effective_field(rate, field_name, next_value) {
            if (!rate || !["effective_from", "effective_to"].includes(field_name)) {
                return;
            }

            rate[String(field_name)] = next_value || "";
        },

        get_next_rate_effective_from(rate) {
            const today_iso = get_today_iso_date();
            const current_effective_from = String(rate?.effective_from || "");

            if (!current_effective_from) {
                return today_iso;
            }

            return current_effective_from >= today_iso
                ? add_days_to_iso_date(current_effective_from, 1)
                : today_iso;
        },

        async save_rate(rate) {
            const normalized_tariff_id = this.get_context_tariff_id();

            if (!this.supports_rate_updates || !normalized_tariff_id || !rate) {
                this.set_save_error("Rate updates are not available with the current API configuration.");
                return;
            }

            this.is_saving = true;
            this.save_error = "";
            this.save_success = "";

            try {
                const tariff = await this.fetch_tariff_detail_json(
                    normalized_tariff_id,
                    {
                        method: "GET",
                        headers: this.get_auth_headers(false)
                    }
                );

                const payload = {
                    tariff: tariff?.id || normalized_tariff_id,
                    charge_components: rate.rates || {},
                    effective_from: rate.effective_from || this.get_next_rate_effective_from(rate),
                    effective_to: rate.effective_to || null,
                    is_active: true
                };

                await this.fetch_rates_json(
                    "update",
                    normalized_tariff_id,
                    {
                        method: "POST",
                        headers: this.get_auth_headers(true),
                        body: JSON.stringify(payload)
                    }
                );

                this.set_save_success("A new tariff rate version was created successfully.");
                await this.load_rates({ preserve_save_success: true });
                this.$emit("updated", "Tariff rates updated successfully.");
            } catch (error) {
                this.set_save_error(error.message || "Unable to create the new tariff rate version.");
            } finally {
                this.is_saving = false;
            }
        }
    }
};
